import { BrandKit } from "@redotech/redo-model/brand-kit";
import { MarketingForgeElementType } from "@redotech/redo-model/content-builder/element-schema";
import { SignupForm } from "@redotech/redo-model/signup-form-schema";
import { memo, NamedExoticComponent, useState } from "react";
import { Flex } from "../flex";
import { SignupFormDiscountCodeDisplay } from "./form-elements/discount-code";
import { FormButton } from "./form-elements/form-button";
import { FormColumn } from "./form-elements/form-column";
import { FormImage } from "./form-elements/form-image";
import { FormElementProps } from "./form-elements/index";
import { Input } from "./form-elements/input";
import { RichText } from "./form-elements/rich-text";
import { TextToSubscribeButton } from "./form-elements/text-to-subscribe-button";
import { TextToSubscribeDescription } from "./form-elements/text-to-subscribe-description";

const elementComponents: Record<
  MarketingForgeElementType,
  NamedExoticComponent<FormElementProps<any>>
> = {
  [MarketingForgeElementType.INPUT]: Input,
  [MarketingForgeElementType.RICH_TEXT]: RichText,
  [MarketingForgeElementType.BUTTON]: FormButton,
  [MarketingForgeElementType.IMAGE]: FormImage,
  [MarketingForgeElementType.COLUMN]: FormColumn,
  [MarketingForgeElementType.DISCOUNT]: SignupFormDiscountCodeDisplay,
  [MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON]: TextToSubscribeButton,
  [MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION]:
    TextToSubscribeDescription,
};

export interface SignupFormProps {
  form: SignupForm;
  currentStep: number;
  brandKit: BrandKit;
  discountCode?: string;
  merchantPhoneNumber?: string;
  anonymousId?: string;
  onClose?: () => void;
  onSubmitStep: (
    stepIndex: number,
    formData: Record<string, string>,
  ) => Promise<void>;
}

export const BaseSignupForm = memo(function BaseSignupForm({
  form,
  brandKit,
  currentStep,
  discountCode,
  merchantPhoneNumber,
  anonymousId,
  onClose,
  onSubmitStep,
}: SignupFormProps) {
  const [submitting, setSubmitting] = useState(false);
  return (
    <div>
      <form
        onSubmit={async (e) => {
          setSubmitting(true);
          e.preventDefault();
          const formData = new FormData(e.currentTarget);
          const transformedData: Record<string, string> = {};
          for (const [key, value] of formData.entries()) {
            transformedData[key] = value as string;
          }
          try {
            await onSubmitStep(currentStep, transformedData);
            setSubmitting(false);
          } catch (e) {
            console.error(e);
            setSubmitting(false);
          }
        }}
      >
        {/* key resets the input element's state when the currentStep changes */}
        <Flex dir="column" gap="xl" key={currentStep} py="xl">
          {form.steps[currentStep].elements.map((element, index) => {
            const ElementComponent = elementComponents[element.type];
            return (
              <div key={index}>
                <ElementComponent
                  anonymousId={anonymousId}
                  backgroundColor={form.layout.backgroundColor}
                  brandKit={brandKit}
                  brandKitOverrides={form.theme.overrides}
                  discountContext={
                    discountCode ? { type: "active", discountCode } : undefined
                  }
                  element={element}
                  layoutType={form.layout.type}
                  merchantPhoneNumber={merchantPhoneNumber}
                  onClose={onClose}
                  submitting={submitting}
                />
              </div>
            );
          })}
        </Flex>
      </form>
    </div>
  );
});
